import "./CreateChampionship.scss";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Button from "../../../Components/Button/Button";
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import newRequest from "../../../Utils/newRequest";
import { message } from "antd";
import { showErrorMessage } from "../../../Utils/showErrorMessage";
import Loader from "../../../Components/Loader/Loader";

const CreateChampionship = () => {
  const { id } = useParams(); // Get championship ID from URL params
  const isEditMode = Boolean(id); // Determine if we're in edit mode

  const [levels, setLevels] = useState([]);
  const [selectedLevels, setSelectedLevels] = useState([]);
  const [newChampionship, setNewChampionship] = useState({});
  const [addCertificate, setAddCertificate] = useState(false);
  const [certificatePreview, setCertificatePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [dataLoadError, setDataLoadError] = useState(false);

  useEffect(() => {
    initializeData();
    return () => {
      if (certificatePreview) {
        URL.revokeObjectURL(certificatePreview);
      }
    };
  }, [id]);

  const initializeData = async () => {
    setInitialLoading(true);
    setDataLoadError(false);
    try {
      await getLevels();
      if (isEditMode) {
        await getChampionshipData();
      }
    } catch (error) {
      console.error("Initialization error:", error);
      setDataLoadError(true);
      showErrorMessage(error);
      // If in edit mode and can't load data, show appropriate message
      if (isEditMode) {
        message.error("فشل في تحميل بيانات البطولة");
      }
    } finally {
      setInitialLoading(false);
    }
  };

  const getLevels = async () => {
    try {
      const res = await newRequest.get("/championship_levels");
      setLevels(res?.data?.data);
    } catch (error) {
      showErrorMessage(error);
    }
  };

  // Helper function to safely get nested properties
  const safeGet = (obj, path, defaultValue = "") => {
    try {
      return (
        path.split(".").reduce((current, key) => current?.[key], obj) ??
        defaultValue
      );
    } catch {
      return defaultValue;
    }
  };

  // Helper function to convert Arabic type to English values
  const convertTypeFromArabic = (typeValue) => {
    if (!typeValue) return "";

    // Decode Unicode if needed and convert to string
    let decodedType = typeValue;
    if (typeof typeValue === "string") {
      try {
        // Try to decode Unicode escape sequences
        decodedType = typeValue.replace(/\\u[\dA-F]{4}/gi, (match) => {
          return String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16));
        });
      } catch (error) {
        console.error("Error decoding type:", error);
        decodedType = typeValue;
      }
    }

    // Check if it contains "عامة" (general in Arabic)
    if (decodedType.includes("عامة")) {
      return "general";
    } else {
      return "special";
    }
  };

  const getChampionshipData = async () => {
    try {
      const res = await newRequest.get(`/championships/${id}`);
      const championshipData = res?.data?.data;

      console.log("Championship data:", championshipData); // Debug log

      // Format datetime for datetime-local input (expects YYYY-MM-DDTHH:MM format)
      const formatDateTime = (dateString) => {
        if (!dateString) return "";
        try {
          const date = new Date(dateString);
          if (isNaN(date.getTime())) return "";

          // Format to YYYY-MM-DDTHH:MM
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          const hours = String(date.getHours()).padStart(2, "0");
          const minutes = String(date.getMinutes()).padStart(2, "0");

          return `${year}-${month}-${day}T${hours}:${minutes}`;
        } catch (error) {
          console.error("Date formatting error:", error);
          return "";
        }
      };

      // Get the raw type value and convert it
      const rawType = safeGet(championshipData, "type");
      const convertedType = convertTypeFromArabic(rawType);

      console.log("Raw type:", rawType, "Converted type:", convertedType); // Debug log

      // Set the form data with proper type conversion and fallbacks
      setNewChampionship({
        title: safeGet(championshipData, "title"),
        price: safeGet(championshipData, "price", "").toString(),
        type: convertedType, // Use the converted type
        start_at: formatDateTime(safeGet(championshipData, "start_at")),
        training_period: safeGet(
          championshipData,
          "training_period",
          ""
        ).toString(),
        question_type_id:
          safeGet(championshipData, "question_type_id") ||
          safeGet(championshipData, "question_type.id"),
        answer_type: safeGet(championshipData, "answer_type"),
        language: safeGet(championshipData, "language"),
        slogan: safeGet(championshipData, "slogan"),
      });

      // Handle levels - support multiple possible structures
      let levelIds = [];
      if (championshipData?.levels) {
        if (Array.isArray(championshipData.levels)) {
          // Case 1: levels is array of objects with id
          if (
            championshipData.levels.length > 0 &&
            typeof championshipData.levels[0] === "object"
          ) {
            levelIds = championshipData.levels
              .map((level) => level.id || level.level_id)
              .filter(Boolean);
          }
          // Case 2: levels is array of IDs
          else if (
            championshipData.levels.length > 0 &&
            typeof championshipData.levels[0] === "number"
          ) {
            levelIds = championshipData.levels;
          }
        }
        // Case 3: levels is a single object
        else if (typeof championshipData.levels === "object") {
          levelIds = [
            championshipData.levels.id || championshipData.levels.level_id,
          ].filter(Boolean);
        }
      }

      // Alternative: check for championship_levels or level_ids fields
      if (levelIds.length === 0) {
        if (
          championshipData?.championship_levels &&
          Array.isArray(championshipData.championship_levels)
        ) {
          levelIds = championshipData.championship_levels
            .map((item) => item.level_id || item.level?.id || item.id)
            .filter(Boolean);
        } else if (
          championshipData?.level_ids &&
          Array.isArray(championshipData.level_ids)
        ) {
          levelIds = championshipData.level_ids;
        }
      }

      setSelectedLevels(levelIds);

      // Handle certificate status - check multiple possible fields
      const hasCertificate = Boolean(
        championshipData?.certificate_url ||
          championshipData?.certificate ||
          championshipData?.has_certificate ||
          championshipData?.addCertificate
      );
      setAddCertificate(hasCertificate);
    } catch (error) {
      console.error("Error fetching championship data:", error);
      showErrorMessage(error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();

      // Add all existing championship data
      Object.keys(newChampionship).forEach((key) => {
        formData.append(key, newChampionship[key]);
      });

      // Add levels
      selectedLevels.forEach((levelId) => {
        formData.append("levels[]", levelId);
      });

      if (addCertificate === true) {
        formData.append("addCertificate", true);
      }

      let response;
      if (isEditMode) {
        // Update existing championship
        formData.append("_method", "PUT");
        response = await newRequest.post(`/championships/${id}`, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        message.success("تم تحديث البطولة بنجاح");
      } else {
        // Create new championship
        response = await newRequest.post("/championships", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
        message.success("تم اضافة البطولة بنجاح");

        // Reset form only when creating
        setSelectedLevels([]);
        setNewChampionship({});
        setCertificatePreview(null);
        setAddCertificate(false);
      }
    } catch (error) {
      showErrorMessage(error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setNewChampionship((prev) => {
      return { ...prev, [name]: value };
    });
  };

  if (initialLoading) {
    return (
      <div className="create">
        <div className="container">
          <Loader />
        </div>
      </div>
    );
  }

  if (dataLoadError && isEditMode) {
    return (
      <div className="create">
        <div className="container">
          <h4 className="title">خطأ في تحميل البيانات</h4>
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <p>فشل في تحميل بيانات البطولة</p>
            <Button
              text="إعادة المحاولة"
              size="large"
              variant="fill"
              onClick={() => initializeData()}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="create">
      <div className="container">
        <h4 className="title">
          {isEditMode ? "تعديل البطولة" : "اضافة بطولة جديدة"}
        </h4>
        {loading ? (
          <Loader />
        ) : (
          <form action="" onSubmit={handleSubmit}>
            <div className="control-group">
              <FormControl sx={{ m: 1, flex: 1 }} className="select">
                <label className="form-label"> اسم البطولة</label>
                <TextField
                  placeholder="أدخل اسم البطولة"
                  fullWidth
                  className="form-input"
                  required
                  name="title"
                  value={newChampionship.title || ""}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl sx={{ m: 1, flex: 1 }} className="select">
                <label className="form-label">سعر البطولة</label>
                <TextField
                  placeholder="أدخل سعر البطولة"
                  fullWidth
                  className="form-input"
                  required
                  type="number"
                  inputProps={{ min: 0, step: "0.01" }}
                  name="price"
                  value={newChampionship.price || ""}
                  onChange={handleChange}
                />
              </FormControl>

              <FormControl sx={{ m: 1, flex: 1 }} className="select">
                <label htmlFor="select">نوع البطولة</label>
                <Select
                  displayEmpty
                  inputProps={{ "aria-label": "Without label" }}
                  required
                  className="select"
                  name="type"
                  value={newChampionship?.type || ""}
                  onChange={handleChange}
                >
                  <MenuItem value="general">عامة</MenuItem>
                  <MenuItem value="special">الطلاب المميزين فقط</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div className="control-group">
              <FormControl sx={{ m: 1, flex: 1 }} className="select">
                <label htmlFor="select">المستويات</label>
                <Select
                  displayEmpty
                  inputProps={{ "aria-label": "Without label" }}
                  required
                  className="select"
                  multiple
                  name="levels"
                  value={selectedLevels.filter((levelId) =>
                    levels.some((level) => level.id === levelId)
                  )} // Ensure only valid level IDs are selected
                  onChange={(e) => setSelectedLevels(e.target.value)}
                  renderValue={(selected) => {
                    if (selected.length === 0) {
                      return (
                        <span style={{ color: "#999" }}>اختر المستويات</span>
                      );
                    }
                    return selected
                      .map((levelId) => {
                        const level = levels.find((l) => l.id === levelId);
                        return level ? level.title : `مستوى ${levelId}`;
                      })
                      .join(", ");
                  }}
                >
                  {levels?.map((item) => (
                    <MenuItem value={item.id} key={item.id}>
                      {item?.title || `مستوى ${item.id}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl sx={{ m: 1, flex: 1 }} className="select">
                <label className="form-label">موعد البطولة</label>
                <TextField
                  placeholder="أدخل موعد البطولة"
                  fullWidth
                  className="form-input"
                  required
                  type="datetime-local"
                  name="start_at"
                  value={newChampionship.start_at || ""}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  inputProps={{
                    step: 60, // 5 min steps
                  }}
                />
              </FormControl>

              <FormControl sx={{ m: 1, flex: 1 }} className="select">
                <label className="form-label">مدة البطولة</label>
                <TextField
                  placeholder="أدخل مدة البطولة بالدقائق"
                  fullWidth
                  className="form-input"
                  required
                  type="number"
                  inputProps={{ min: 1, step: 1 }}
                  name="training_period"
                  value={newChampionship.training_period || ""}
                  onChange={handleChange}
                />
              </FormControl>
            </div>

            <div className="control-group">
              <FormControl sx={{ m: 1, flex: 1 }} className="select">
                <label htmlFor="select">نوع المسائل</label>
                <Select
                  displayEmpty
                  inputProps={{ "aria-label": "Without label" }}
                  required
                  className="select"
                  name="question_type_id"
                  value={newChampionship.question_type_id || ""}
                  onChange={handleChange}
                >
                  <MenuItem value={1}>رقم رقم</MenuItem>
                  <MenuItem value={2}>مسألة كاملة</MenuItem>
                  <MenuItem value={3}>ظهور مسألتين</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ m: 1, flex: 1 }} className="select">
                <label htmlFor="select">نوع الاجابة</label>
                <Select
                  displayEmpty
                  inputProps={{ "aria-label": "Without label" }}
                  required
                  className="select"
                  name="answer_type"
                  value={newChampionship?.answer_type || ""}
                  onChange={handleChange}
                >
                  <MenuItem value="choice">اختياري</MenuItem>
                  <MenuItem value="text">كتابة نص</MenuItem>
                </Select>
              </FormControl>

              <FormControl sx={{ m: 1, flex: 1 }} className="select">
                <label htmlFor="select">لغة البطولة</label>
                <Select
                  displayEmpty
                  inputProps={{ "aria-label": "Without label" }}
                  required
                  className="select"
                  name="language"
                  value={newChampionship.language || ""}
                  onChange={handleChange}
                >
                  <MenuItem value="ar">عربي</MenuItem>
                  <MenuItem value="en">انجليزي</MenuItem>
                </Select>
              </FormControl>
            </div>

            <div className="control-group">
              <FormControl sx={{ m: 1, flex: 1 }} className="select">
                <label className="form-label">الجمله التعريفيه</label>
                <TextField
                  placeholder="الجمله التعريفيه"
                  multiline
                  rows={5}
                  maxRows={5}
                  name="slogan"
                  value={newChampionship.slogan || ""}
                  onChange={handleChange}
                />
              </FormControl>
            </div>

            <div className="control-group">
              <FormControl sx={{ flex: 1 }} className="select">
                <FormControlLabel
                  sx={{ margin: "0" }}
                  control={
                    <Checkbox
                      checked={addCertificate}
                      onChange={(e) => setAddCertificate(e.target.checked)}
                    />
                  }
                  label="اضافة شهادة"
                />
              </FormControl>
            </div>

            <div className="form-actions">
              <Button
                text={isEditMode ? "تحديث" : "اضافة"}
                size="large"
                variant="fill"
              />
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateChampionship;
